// © 2024–2026 Jos Jozef Sytze van der Werf — Alle rechten voorbehouden.
/**
 * Step14Messages - Wizard stap 15: Berichtenservice
 * V35-P1: ContactPicker met WilsApp-contacten + demo-telefoonboek.
 * Dezelfde opslag als MessagesPage - geen dubbel datamodel.
 */
import { useState, useEffect, useCallback } from 'react'
import { Plus, Pencil, Trash2, X, CheckCircle2, Clock,
         MessageSquare, Mail, Copy, RefreshCw,
         Users, Smile, UserCheck, Smartphone, UserPlus } from 'lucide-react'
import { usePhoneContacts } from '@/hooks/usePhoneContacts'
import { Card }      from '@/components/ui/Card'
import { Button, Input, Textarea } from '@/components/ui'
import { Select }    from '@/components/ui/Select'
import { Badge }     from '@/components/ui/Badge'
import { InfoBlock }  from '@/components/ui/InfoBlock'
import { useAuthStore } from '@/store'
import { useToast }  from '@/hooks/useToast'
import { loadMessages, saveMessage, deleteMessage, type MessageDraft } from '@/services/wishes'
import { loadPartner, loadChildren } from '@/services/wizard'
import { loadTrustedPersons } from '@/services/trustedPerson'
import { truncate }  from '@/lib/utils'
import type { SaveStatus } from '@/hooks/useAutosave'

// ── Constanten ───────────────────────────────────────────────────────────────


const SEND_MOMENTS = [
  { value: 'direct',  label: 'Direct na activering' },
  { value: '24h',     label: '24 uur na overlijden' },
  { value: '48h',     label: '48 uur na overlijden' },
  { value: '7days',   label: '7 dagen na overlijden' },
  { value: 'funeral', label: 'Na de uitvaart' },
  { value: 'custom',  label: 'Eigen moment' },
]


// ── Types ────────────────────────────────────────────────────────────────────

interface KnownContact {
  group:     'partner' | 'children' | 'vp' | 'other' | 'phone' | 'new'
  init:      string
  initColor: string
  name:      string
  phone:     string
  email:     string
}

interface ContactGroup {
  id:      KnownContact['group']
  label:   string
  icon:    React.ReactNode
  count:   number
  contacts: KnownContact[]
}

interface MsgForm {
  recipientLabel: string
  recipientPhone: string
  recipientEmail: string
  sendMethod:     string
  sendMoment:     string
  customMoment:   string
  content:        string
}

const EMPTY_FORM: MsgForm = {
  recipientLabel: '', recipientPhone: '', recipientEmail: '',
  sendMethod: 'whatsapp', sendMoment: '24h', customMoment: '', content: '',
}

// ── Helpers ──────────────────────────────────────────────────────────────────

function initials(name: string): string {
  return name.trim().split(/\s+/).slice(0, 2).map(w => w[0]?.toUpperCase() ?? '').join('')
}

function cleanPhone(raw: string): string {
  const digits = raw.replace(/[^0-9+]/g, '')
  if (digits.startsWith('06'))  return '+316' + digits.slice(2)
  if (digits.startsWith('00'))  return '+' + digits.slice(2)
  return digits
}

// ── SendActions ──────────────────────────────────────────────────────────────

function SendActions({ message }: { message: MessageDraft }) {
  const phone   = (message.recipientPhone ?? '').trim()
  const email   = (message.recipientEmail ?? '').trim()
  const content = message.content ?? ''
  const name    = message.recipientLabel ?? 'WilsApp'
  const [copied, setCopied] = useState(false)

  async function copyText() {
    try { await navigator.clipboard.writeText(content) }
    catch { const el = document.createElement('textarea'); el.value = content; document.body.appendChild(el); el.select(); document.execCommand('copy'); document.body.removeChild(el) }
    setCopied(true); setTimeout(() => setCopied(false), 2000)
  }

  const hasPhone = !!phone
  const hasEmail = !!email

  return (
    <div className="mt-2 flex flex-col gap-1.5">
      <p className="text-xs text-ink-4">WilsApp opent uw gekozen app. U controleert en verzendt het bericht zelf.</p>
      <div className="flex flex-wrap gap-1.5">
        <button type="button" disabled={!hasPhone}
          onClick={() => hasPhone && window.open(`https://wa.me/${cleanPhone(phone)}?text=${encodeURIComponent(content)}`, '_blank', 'noopener')}
          className={`flex items-center gap-1 rounded-lg px-2.5 py-1 text-xs font-medium transition-colors ${hasPhone ? 'bg-[#25D366] text-white hover:bg-[#1ebe5d]' : 'bg-sand-200 text-ink-4 cursor-not-allowed'}`}>
          <MessageSquare className="h-3 w-3" />WhatsApp{!hasPhone && <span className="opacity-70 ml-0.5 text-[10px]">- geen nr</span>}
        </button>
        <button type="button" disabled={!hasPhone}
          onClick={() => hasPhone && (window.location.href = `sms:${cleanPhone(phone)}?body=${encodeURIComponent(content)}`)}
          className={`flex items-center gap-1 rounded-lg px-2.5 py-1 text-xs font-medium transition-colors ${hasPhone ? 'bg-blue-500 text-white hover:bg-blue-600' : 'bg-sand-200 text-ink-4 cursor-not-allowed'}`}>
          <MessageSquare className="h-3 w-3" />SMS{!hasPhone && <span className="opacity-70 ml-0.5 text-[10px]">- geen nr</span>}
        </button>
        <button type="button" disabled={!hasEmail}
          onClick={() => hasEmail && (window.location.href = `mailto:${email}?subject=${encodeURIComponent(`Bericht van ${name}`)}&body=${encodeURIComponent(content)}`)}
          className={`flex items-center gap-1 rounded-lg px-2.5 py-1 text-xs font-medium transition-colors ${hasEmail ? 'bg-orange-500 text-white hover:bg-orange-600' : 'bg-sand-200 text-ink-4 cursor-not-allowed'}`}>
          <Mail className="h-3 w-3" />E-mail{!hasEmail && <span className="opacity-70 ml-0.5 text-[10px]">- geen adres</span>}
        </button>
        <button type="button" onClick={copyText}
          className="flex items-center gap-1 rounded-lg border border-sand-300 bg-white px-2.5 py-1 text-xs font-medium text-ink-3 hover:bg-sand-50 transition-colors">
          {copied ? <><CheckCircle2 className="h-3 w-3 text-sage-600" /><span className="text-sage-600">Gekopieerd</span></> : <><Copy className="h-3 w-3" />Kopieer</>}
        </button>
      </div>
    </div>
  )
}

// ── Telefooncontacten-paneel ─────────────────────────────────────────────────

interface PhonePickerProps {
  supported:   boolean
  status:      string
  sourceLabel: string
  onImport:    () => void
}

/** Permissie-intro / verbonden-status / iOS-terugval voor de telefooncontacten-bron. */
function PhonePanel({ phone, count, onClose }: { phone: PhonePickerProps; count: number; onClose: () => void }) {
  // iOS Safari / desktop: geen webbron beschikbaar → nette terugval, knop blijft gereserveerd.
  if (!phone.supported) {
    return (
      <div className="flex flex-col gap-2">
        <p className="text-xs font-semibold text-sage-700 flex items-center gap-1.5"><Smartphone className="h-4 w-4" /> Telefooncontacten</p>
        <p className="text-xs text-ink-3 leading-relaxed">
          Op iPhone is directe toegang tot telefooncontacten vanuit een webapp helaas niet mogelijk.
          U kunt wel bestaande WilsApp-contacten gebruiken of handmatig een contact toevoegen.
        </p>
      </div>
    )
  }
  // Verbonden: toon status + vernieuwen.
  if (count > 0) {
    return (
      <div className="flex items-center justify-between mb-1">
        <p className="text-xs font-semibold text-sage-700 flex items-center gap-1.5">
          <span className="inline-block h-2 w-2 rounded-full bg-[#25D366]" /> Verbonden - {count} {count === 1 ? 'contact' : 'contacten'}
        </p>
        <button type="button" onClick={phone.onImport} disabled={phone.status === 'importing'}
          className="flex items-center gap-1 text-xs text-sage-600 hover:text-sage-800 disabled:opacity-60">
          <RefreshCw className={`h-3 w-3 ${phone.status === 'importing' ? 'animate-spin' : ''}`} /> Vernieuwen
        </button>
      </div>
    )
  }
  // Eerste keer: permissie-intro (privacy), dan opent de echte OS-contactkiezer.
  return (
    <div className="flex flex-col gap-2">
      <p className="text-xs font-semibold text-sage-700 flex items-center gap-1.5"><Smartphone className="h-4 w-4" /> Toegang tot uw contacten</p>
      <p className="text-xs text-ink-3 leading-relaxed">
        WilsApp kan uw telefooncontacten gebruiken zodat u eenvoudig berichten kunt voorbereiden.
        Uw contacten blijven op uw toestel. Er wordt niets automatisch verzonden.
      </p>
      {phone.status === 'error' && <p className="text-xs text-red-600">Kon de contacten niet ophalen. Probeer het opnieuw.</p>}
      <div className="flex gap-2">
        <button type="button" onClick={phone.onImport} disabled={phone.status === 'importing'}
          className="rounded-lg bg-moss px-3 py-1.5 text-xs font-semibold text-white hover:bg-moss-light transition-all active:scale-[0.97] disabled:opacity-60">
          {phone.status === 'importing' ? 'Bezig…' : 'Toestaan'}
        </button>
        <button type="button" onClick={onClose}
          className="rounded-lg border border-sand-300 bg-white px-3 py-1.5 text-xs font-medium text-ink-3 hover:bg-sand-50 transition-all active:scale-[0.97]">
          Niet nu
        </button>
      </div>
    </div>
  )
}

// ── ContactPicker ────────────────────────────────────────────────────────────

function ContactPicker({
  groups, selectedContact, onSelect, phone,
}: {
  groups:          ContactGroup[]
  selectedContact: KnownContact | null
  onSelect:        (c: KnownContact) => void
  phone?:          PhonePickerProps
}) {
  const [activeGroup, setActiveGroup] = useState<KnownContact['group'] | null>(null)

  function handleTileClick(g: ContactGroup) {
    if (g.id === 'new') {
      onSelect({ group: 'new', init: '?', initColor: 'bg-sand-200 text-ink-4', name: '', phone: '', email: '' })
      setActiveGroup(null)
      return
    }
    if (g.id !== 'phone' && g.contacts.length === 1) {
      onSelect(g.contacts[0])
      setActiveGroup(null)
      return
    }
    setActiveGroup(prev => prev === g.id ? null : g.id)
  }

  const openGroup = groups.find(g => g.id === activeGroup)

  return (
    <div className="flex flex-col gap-3">
      {/* Tegel-rij */}
      <div className="grid grid-cols-3 gap-2 sm:grid-cols-6">
        {groups.map(g => {
          const isActive = activeGroup === g.id
          const isSelected = selectedContact?.group === g.id && activeGroup !== g.id
          return (
            <button
              key={g.id}
              type="button"
              onClick={() => handleTileClick(g)}
              className={`flex flex-col items-center gap-1.5 rounded-xl border-2 px-2 py-3 text-center transition-all min-w-0
                ${g.id === 'new' ? 'border-dashed' : ''}
                ${isActive    ? 'border-sage-600 bg-sage-50' :
                  isSelected  ? 'border-sage-400 bg-sage-50/60' :
                                'border-sand-200 bg-white hover:border-sage-300 hover:bg-sage-50/40'}`}
            >
              <div className={`flex h-9 w-9 items-center justify-center rounded-full
                ${g.id === 'partner'  ? 'bg-violet-100 text-violet-600' :
                  g.id === 'children' ? 'bg-amber-100 text-amber-600' :
                  g.id === 'vp'       ? 'bg-sage-100 text-sage-600' :
                  g.id === 'other'    ? 'bg-red-50 text-red-500' :
                  g.id === 'phone'    ? 'bg-blue-100 text-blue-600' :
                                        'bg-sand-200 text-ink-4'}`}>
                {g.icon}
              </div>
              <span className="text-[11px] font-medium text-ink leading-tight w-full break-words">{g.label}</span>
              {g.id !== 'new' && <span className="text-[10px] text-ink-4">{g.count} {g.count === 1 ? 'persoon' : 'personen'}</span>}
            </button>
          )
        })}
      </div>

      {/* Sub-lijst */}
      {openGroup && (
        <div className="rounded-xl border border-sage-200 bg-sage-50/50 p-3 animate-slide-down">
          {openGroup.id === 'phone' && phone ? (
            <PhonePanel phone={phone} count={openGroup.contacts.length} onClose={() => setActiveGroup(null)} />
          ) : (
            <p className="text-xs font-semibold text-sage-700 mb-2 flex items-center gap-1.5">
              {openGroup.icon}
              <span>Kies een persoon uit {openGroup.label.toLowerCase()}</span>
            </p>
          )}
          {openGroup.contacts.length > 0 && (
          <div className="flex flex-col gap-1 max-h-48 overflow-y-auto mt-2">
            {openGroup.contacts.map((c, i) => (
              <button
                key={i}
                type="button"
                onClick={() => { onSelect(c); setActiveGroup(null) }}
                className={`flex items-center gap-3 rounded-lg px-3 py-2 text-left transition-all
                  ${selectedContact?.name === c.name ? 'bg-sage-100 border border-sage-300' : 'bg-white border border-sand-200 hover:border-sage-300 hover:bg-sage-50/50'}`}
              >
                <div className={`flex h-8 w-8 items-center justify-center rounded-full text-xs font-semibold flex-shrink-0 ${c.initColor}`}>
                  {c.init}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-ink truncate">{c.name}</p>
                  <p className="text-xs text-ink-4 truncate">{c.phone || c.email || 'Geen contactgegevens'}</p>
                </div>
                {selectedContact?.name === c.name && (
                  <CheckCircle2 className="h-4 w-4 text-sage-600 flex-shrink-0" />
                )}
              </button>
            ))}
          </div>
          )}
        </div>
      )}
    </div>
  )
}

// ── Hoofdcomponent ───────────────────────────────────────────────────────────

export function Step14Messages({ onSaveStatus }: { onSaveStatus: (s: SaveStatus) => void }) {
  const { userId }  = useAuthStore()
  const { toast }   = useToast()
  const [msgs, setMsgs]             = useState<MessageDraft[]>([])
  const [loaded, setLoaded]         = useState(false)
  const [showForm, setShowForm]     = useState(false)
  const [editId, setEditId]         = useState<string | null>(null)
  const [form, setForm]             = useState<MsgForm>(EMPTY_FORM)
  const [isSaving, setIsSaving]     = useState(false)
  const [contactGroups, setContactGroups] = useState<ContactGroup[]>([])
  const [selectedContact, setSelectedContact] = useState<KnownContact | null>(null)

  // ── Telefooncontacten via de generieke ContactProvider-laag ──
  const phone = usePhoneContacts()
  const phoneKnown: KnownContact[] = phone.contacts.map(c => ({
    group: 'phone', init: initials(c.name), initColor: 'bg-blue-100 text-blue-700',
    name: c.name, phone: c.phone ?? '', email: c.email ?? '',
  }))
  // Telefoongroep altijd tonen (knop gereserveerd), ingevoegd vóór 'Nieuwe ontvanger'.
  const displayGroups: ContactGroup[] = [
    ...contactGroups.filter(g => g.id !== 'new'),
    { id: 'phone', label: 'Telefooncontacten', icon: <Smartphone className="h-4 w-4" />, count: phoneKnown.length, contacts: phoneKnown },
    ...contactGroups.filter(g => g.id === 'new'),
  ]
  // Ontdubbel geïmporteerde contacten tegen partner/kind/vertrouwenspersoon.
  const existingForDedupe = contactGroups.flatMap(g => g.contacts)

  useEffect(() => { onSaveStatus('idle') }, [onSaveStatus])

  // Laad bestaande WilsApp-contacten
  const loadContacts = useCallback(async () => {
    if (!userId) return
    const groups: ContactGroup[] = []

    // Partner
    const partner = await loadPartner(userId)
    const partnerContacts: KnownContact[] = []
    if (partner?.hasPartner === 'yes' && partner.name?.trim()) {
      partnerContacts.push({
        group: 'partner',
        init: initials(partner.name),
        initColor: 'bg-violet-100 text-violet-600',
        name: partner.name,
        phone: partner.phone ?? '',
        email: partner.email ?? '',
      })
    }
    if (partnerContacts.length > 0)
      groups.push({ id: 'partner', label: 'Partner', icon: <Users className="h-4 w-4" />, count: partnerContacts.length, contacts: partnerContacts })

    // Kinderen
    const children = await loadChildren(userId)
    const childContacts: KnownContact[] = []
    if (children?.hasChildren === 'yes') {
      children.children.filter(c => c.name?.trim()).forEach(c => {
        childContacts.push({
          group: 'children',
          init: initials(c.name),
          initColor: 'bg-amber-100 text-amber-600',
          name: c.name,
          phone: '',
          email: '',
        })
      })
    }
    if (childContacts.length > 0)
      groups.push({ id: 'children', label: 'Kinderen', icon: <Smile className="h-4 w-4" />, count: childContacts.length, contacts: childContacts })

    // Vertrouwenspersonen
    const vps = await loadTrustedPersons(userId)
    const vpContacts: KnownContact[] = vps
      .filter(v => v.confirmationStatus !== 'REVOKED')
      .map(v => ({
        group: 'vp' as const,
        init: initials(v.firstName),
        initColor: 'bg-sage-100 text-sage-600',
        name: v.firstName,
        phone: '',
        email: v.email ?? '',
      }))
    if (vpContacts.length > 0)
      groups.push({ id: 'vp', label: 'Vertrouwenspersonen', icon: <UserCheck className="h-4 w-4" />, count: vpContacts.length, contacts: vpContacts })

    // Telefooncontacten worden dynamisch toegevoegd in de render (zie displayGroups),
    // op basis van de ContactProvider-laag - niet langer een vast demo-telefoonboek.

    // Nieuwe ontvanger altijd als laatste
    groups.push({ id: 'new', label: 'Nieuwe ontvanger', icon: <UserPlus className="h-4 w-4" />, count: 0, contacts: [] })

    setContactGroups(groups)
  }, [userId])

  useEffect(() => { loadContacts() }, [loadContacts])

  async function reload() { if (userId) setMsgs(await loadMessages(userId)); setLoaded(true) }
  useEffect(() => { reload() }, [userId])

  function handleContactSelect(c: KnownContact) {
    setSelectedContact(c)
    setForm(p => ({
      ...p,
      recipientLabel: c.name,
      recipientPhone: c.phone,
      recipientEmail: c.email,
    }))
  }

  function openNew() {
    setForm(EMPTY_FORM)
    setEditId(null)
    setSelectedContact(null)
    setShowForm(true)
  }

  function openEdit(m: MessageDraft) {
    setForm({
      recipientLabel: m.recipientLabel,
      recipientPhone: m.recipientPhone ?? '',
      recipientEmail: m.recipientEmail ?? '',
      sendMethod:     m.sendMethod    ?? 'whatsapp',
      sendMoment:     m.sendMoment    ?? '24h',
      customMoment:   m.customMoment  ?? '',
      content:        m.content,
    })
    setSelectedContact(null)
    setEditId(m.id)
    setShowForm(true)
  }

  async function handleSave(ready = false) {
    if (!userId || !form.recipientLabel.trim() || !form.content.trim()) return
    setIsSaving(true)
    try {
      await saveMessage(userId, {
        recipientLabel: form.recipientLabel,
        content:        form.content,
        status:         ready ? 'READY' : 'DRAFT',
        recipientPhone: form.recipientPhone,
        recipientEmail: form.recipientEmail,
        sendMethod:     form.sendMethod,
        sendMoment:     form.sendMoment,
        customMoment:   form.customMoment,
      }, editId ?? undefined)
      setShowForm(false); setEditId(null); setForm(EMPTY_FORM); setSelectedContact(null)
      await reload()
    } catch { toast.error('Opslaan mislukt') }
    finally { setIsSaving(false) }
  }

  async function handleDelete(id: string) {
    if (!window.confirm('Verwijderen?')) return
    await deleteMessage(id); await reload()
  }

  // V47-P1: geen lege introductiepagina meer. Zonder berichten opent direct het formulier;
  // met berichten toont de lijst (met '+ Nieuw bericht' bovenaan).
  const formVisible = showForm || (loaded && msgs.length === 0)

  return (
    <div className="flex flex-col gap-5">
      <div>
        <h2 className="text-xl font-semibold text-ink mb-1">Berichtenservice</h2>
        <p className="text-sm text-ink-3 leading-relaxed">
          Schrijf nu alvast persoonlijke berichten voor uw naasten - uw vertrouwenspersoon zorgt dat zij deze ontvangen wanneer het zover is.
        </p>
      </div>

      {/* Uitleg - positief InfoBlock i.p.v. waarschuwing */}
      <InfoBlock title="Hoe werkt dit?" variant="emotional" defaultOpen={false}>
        <p>U schrijft een persoonlijk bericht voor een specifieke persoon - uw partner, een kind, een vriend. U kiest wanneer het verstuurd wordt: direct na uw overlijden, of op een later moment.</p>
        <p>Uw <strong>vertrouwenspersoon</strong> verstuurt het bericht namens u, via WhatsApp, sms of e-mail. WilsApp verstuurt nooit automatisch - er is altijd iemand die het initiatief neemt.</p>
        <p><em>Voorbeelden: een afscheidsbrief aan uw kinderen, praktische instructies voor uw partner, of een dankjewel aan een goede vriend.</em></p>
      </InfoBlock>

      {/* Formulier */}
      {formVisible && (
        <Card className="border-sage-200 bg-sage-50/50">
          <div className="flex items-center justify-between mb-4">
            <p className="text-sm font-semibold text-ink">{editId ? 'Bericht bewerken' : 'Nieuw bericht'}</p>
            <button onClick={() => { setShowForm(false); setEditId(null); setSelectedContact(null) }}
              className="text-ink-4 hover:text-ink-3"><X className="h-4 w-4" /></button>
          </div>
          <div className="flex flex-col gap-4">

            {/* Stap 1: Contact kiezen */}
            {!editId && contactGroups.length > 0 && (
              <div className="flex flex-col gap-2">
                <p className="text-xs font-semibold text-sage-700 flex items-center gap-1.5">
                  <span className="flex h-4 w-4 items-center justify-center rounded-full bg-sage-600 text-white text-[10px] font-bold">1</span>
                  Selecteer een contact
                </p>
                <ContactPicker
                  groups={displayGroups}
                  selectedContact={selectedContact}
                  onSelect={handleContactSelect}
                  phone={{
                    supported:   phone.supported,
                    status:      phone.status,
                    sourceLabel: phone.sourceLabel,
                    onImport:    () => phone.importContacts(existingForDedupe),
                  }}
                />
              </div>
            )}

            {/* Stap 2 + 3 - twee kolommen */}
            <div className="grid gap-4 sm:grid-cols-2">

              {/* Links: ontvanger */}
              <div className="flex flex-col gap-3">
                <p className="text-xs font-semibold text-sage-700 flex items-center gap-1.5">
                  <span className="flex h-4 w-4 items-center justify-center rounded-full bg-sage-600 text-white text-[10px] font-bold flex-shrink-0">{editId ? '1' : '2'}</span>
                  Ontvanger gegevens
                </p>

                {selectedContact?.name && (
                  <div className="flex items-center gap-2 rounded-xl bg-sage-50 border border-sage-200 px-3 py-2.5">
                    <div className={`flex h-8 w-8 items-center justify-center rounded-full text-xs font-semibold flex-shrink-0 ${selectedContact.initColor}`}>
                      {selectedContact.init}
                    </div>
                    <span className="text-sm font-medium text-sage-800 flex-1 truncate">Geselecteerd: {selectedContact.name}</span>
                    <CheckCircle2 className="h-4 w-4 text-sage-600 flex-shrink-0" />
                  </div>
                )}

                <Input label="Naam ontvanger" value={form.recipientLabel}
                  onChange={e => setForm(p => ({...p, recipientLabel: e.target.value}))}
                  placeholder="Bijv: Mijn dochter Emma" required />

                <Input label="Mobiel nummer (optioneel)" type="tel" value={form.recipientPhone}
                  onChange={e => setForm(p => ({...p, recipientPhone: e.target.value}))}
                  placeholder="06-..." hint="Voor WhatsApp of SMS" />

                <Input label="E-mail (optioneel)" type="email" value={form.recipientEmail}
                  onChange={e => setForm(p => ({...p, recipientEmail: e.target.value}))}
                  placeholder="naam@mail.nl" />

                {selectedContact?.name && (
                  <p className="text-xs text-ink-4 flex items-center gap-1">
                    <span className="text-blue-400 text-sm">ⓘ</span>
                    Automatisch ingevuld - pas aan indien nodig.
                  </p>
                )}
              </div>

              {/* Rechts: verzendkanaal */}
              <div className="flex flex-col gap-3">
                <p className="text-xs font-semibold text-sage-700 flex items-center gap-1.5">
                  <span className="flex h-4 w-4 items-center justify-center rounded-full bg-sage-600 text-white text-[10px] font-bold flex-shrink-0">{editId ? '2' : '3'}</span>
                  Bericht verzenden via
                </p>

                <button type="button" onClick={() => setForm(p => ({...p, sendMethod: 'whatsapp'}))}
                  className={`flex items-center gap-3 rounded-xl border-2 px-4 py-3 text-left transition-all ${
                    form.sendMethod === 'whatsapp' || form.sendMethod === 'all'
                      ? 'border-sage-600 bg-sage-50' : 'border-sand-200 bg-white hover:border-sage-300'}`}>
                  <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-[#25D366] flex-shrink-0">
                    <MessageSquare className="h-5 w-5 text-white" />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-semibold text-ink flex items-center gap-2">
                      WhatsApp
                      {(form.sendMethod === 'whatsapp' || form.sendMethod === 'all') &&
                        <span className="text-[10px] font-medium bg-sage-100 text-sage-700 px-2 py-0.5 rounded-full">Aanbevolen</span>}
                    </p>
                    <p className="text-xs text-ink-4">Snel en persoonlijk</p>
                  </div>
                  <div className={`h-4 w-4 rounded-full border-2 flex-shrink-0 ${
                    form.sendMethod === 'whatsapp' || form.sendMethod === 'all'
                      ? 'border-sage-600 bg-sage-600' : 'border-sand-300'}`} />
                </button>

                <button type="button" onClick={() => setForm(p => ({...p, sendMethod: 'email'}))}
                  className={`flex items-center gap-3 rounded-xl border-2 px-4 py-3 text-left transition-all ${
                    form.sendMethod === 'email' ? 'border-sage-600 bg-sage-50' : 'border-sand-200 bg-white hover:border-sage-300'}`}>
                  <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-orange-500 flex-shrink-0">
                    <Mail className="h-5 w-5 text-white" />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-semibold text-ink">E-mail</p>
                    <p className="text-xs text-ink-4">Veilig en betrouwbaar</p>
                  </div>
                  <div className={`h-4 w-4 rounded-full border-2 flex-shrink-0 ${
                    form.sendMethod === 'email' ? 'border-sage-600 bg-sage-600' : 'border-sand-300'}`} />
                </button>

                <button type="button" onClick={() => setForm(p => ({...p, sendMethod: 'sms'}))}
                  className={`flex items-center gap-3 rounded-xl border-2 px-4 py-3 text-left transition-all ${
                    form.sendMethod === 'sms' ? 'border-sage-600 bg-sage-50' : 'border-sand-200 bg-white hover:border-sage-300'}`}>
                  <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-blue-500 flex-shrink-0">
                    <MessageSquare className="h-5 w-5 text-white" />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-semibold text-ink">SMS</p>
                    <p className="text-xs text-ink-4">Altijd bezorgd</p>
                  </div>
                  <div className={`h-4 w-4 rounded-full border-2 flex-shrink-0 ${
                    form.sendMethod === 'sms' ? 'border-sage-600 bg-sage-600' : 'border-sand-300'}`} />
                </button>

                <Select label="Wanneer" options={SEND_MOMENTS} value={form.sendMoment}
                  onChange={e => setForm(p => ({...p, sendMoment: e.target.value}))} />
                {form.sendMoment === 'custom' && (
                  <div className="animate-slide-down">
                    <Input label="Gewenst moment" value={form.customMoment}
                      onChange={e => setForm(p => ({...p, customMoment: e.target.value}))}
                      placeholder="Bijv: 3 maanden na overlijden..." />
                  </div>
                )}
              </div>
            </div>

            {/* Inhoud */}
            <Textarea label="Uw bericht" value={form.content}
              onChange={e => setForm(p => ({...p, content: e.target.value}))}
              placeholder="Schrijf hier uw bericht..." maxLength={2000} showCount
              className="min-h-[140px]" />

            <div className="flex items-start gap-3 rounded-xl bg-amber-50 border border-amber-200 px-4 py-3">
              <span className="text-base flex-shrink-0">💡</span>
              <p className="text-xs text-amber-800 leading-relaxed">
                <strong>Goed om te weten:</strong> Uw vertrouwenspersoon ontvangt een melding en beslist wanneer dit bericht wordt verzonden.
              </p>
            </div>

            <div className="flex gap-3">
              <Button fullWidth onClick={() => handleSave(false)} loading={isSaving} variant="secondary">Concept opslaan</Button>
              <Button fullWidth onClick={() => handleSave(true)} loading={isSaving} icon={<CheckCircle2 className="h-4 w-4" />}>Klaar</Button>
            </div>
          </div>
        </Card>
      )}

      {!formVisible && msgs.length > 0 && (
        <div className="flex justify-end">
          <Button size="sm" onClick={openNew} icon={<Plus className="h-4 w-4" />}>Nieuw bericht</Button>
        </div>
      )}

      {/* Berichtenlijst */}
      <div className="flex flex-col gap-2">
        {msgs.map(m => (
          <Card key={m.id} padding="sm">
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0 mt-0.5">
                {m.status === 'READY'
                  ? <CheckCircle2 className="h-4 w-4 text-sage-600" />
                  : <Clock        className="h-4 w-4 text-ink-4" />}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <p className="text-sm font-medium text-ink break-words">{m.recipientLabel}</p>
                  <Badge variant={m.status === 'READY' ? 'success' : 'muted'} size="sm">
                    {m.status === 'READY' ? 'Klaar' : 'Concept'}
                  </Badge>
                  {m.sendMoment && (
                    <Badge variant="muted" size="sm">
                      {SEND_MOMENTS.find(s => s.value === m.sendMoment)?.label ?? m.sendMoment}
                    </Badge>
                  )}
                </div>
                {m.content && <p className="text-xs text-ink-4 mt-0.5 break-words">{truncate(m.content, 80)}</p>}
                <SendActions message={m} />
              </div>
              <div className="flex gap-1 flex-shrink-0">
                <button onClick={() => openEdit(m)}
                  className="flex h-7 w-7 items-center justify-center rounded-lg text-ink-4 hover:bg-sand-100">
                  <Pencil className="h-3.5 w-3.5" />
                </button>
                <button onClick={() => handleDelete(m.id)}
                  className="flex h-7 w-7 items-center justify-center rounded-lg text-ink-4 hover:bg-status-alertBg hover:text-status-alertText">
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>
            </div>
          </Card>
        ))}
      </div>

    </div>
  )
}
